import { useState, useCallback, useMemo } from 'react'
import { useLazyQuery } from '@apollo/client'

import { GET_COMPANY_TAGS } from 'src/graphql/companyTags.graphql'
import { toast } from 'src/components/Overlays/Toast/Toast'

import { FilterOption } from '../types'

interface UseTagsFilterProps {
  selectedTags: string[]
  onTagsChange: (tags: string[]) => void
}

export const useTagsFilter = ({
  selectedTags,
  onTagsChange,
}: UseTagsFilterProps) => {
  const [options, setOptions] = useState<FilterOption[]>([])
  const [isLoaded, setIsLoaded] = useState(false)

  const onError = useCallback(() => {
    toast('Failed to load tags', 'error')
  }, [])

  const [loadTags, { loading }] = useLazyQuery(GET_COMPANY_TAGS, {
    variables: {
      pageInfo: { offset: 0, limit: 100 },
    },
    onCompleted: (data) => {
      const tagOptions =
        data?.companyTags?.items
          ?.filter((tag) => tag?.label && tag?.tagId) // Filter out invalid tags
          ?.map((tag) => ({
            label: tag.label,
            value: tag.tagId,
          })) || []
      setOptions(tagOptions)
      setIsLoaded(true)
    },
    onError,
  })

  // Update options to reflect selected state
  const optionsWithSelection = useMemo(() => {
    return options.map((option) => ({
      ...option,
      selected: selectedTags.includes(option.value),
    }))
  }, [options, selectedTags])

  const loadData = useCallback(() => {
    if (!isLoaded && !loading) {
      loadTags()
    }
  }, [isLoaded, loading, loadTags])

  return {
    options: optionsWithSelection,
    selected: selectedTags,
    onChange: onTagsChange,
    loadData,
    loading,
    isLoaded,
  }
}
